import type { CampaignTemplate } from '@iws/theme-schema/campaign'

import { BusinessModel } from '@/http/models/business/Business'
import { configService } from '@/services'

/**
 * Campaign templates the merchant saved, stored with the store in business_profile.theme_settings
 * (`campaign_templates`, an additive key). The theme editor never sees this key (see ThemeBuilderPage),
 * and every write re-reads the latest settings first so neither editor overwrites the other.
 */
export const CAMPAIGN_TEMPLATES_KEY = 'campaign_templates'

type ThemeSettings = Record<string, unknown>

const latestThemeSettings = async (): Promise<ThemeSettings> => {
  const res = await BusinessModel.getBusinessProfile()
  if (!res.success) throw new Error('Could not load the store settings')
  return ((res.data?.result?.theme_settings as ThemeSettings | undefined) ?? {})
}

const templatesIn = (settings: ThemeSettings): CampaignTemplate[] => {
  const list = settings[CAMPAIGN_TEMPLATES_KEY]
  return Array.isArray(list) ? (list as CampaignTemplate[]) : []
}

/** Templates saved before they were stored on the server lived in this browser. */
const legacyKey = () => `campaign-templates:${configService.getConfig()?.apiConfig?.clientHost ?? 'store'}`
const takeLegacy = (): CampaignTemplate[] => {
  try {
    const list = JSON.parse(localStorage.getItem(legacyKey()) ?? '[]') as CampaignTemplate[]
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

const write = async (update: (current: CampaignTemplate[]) => CampaignTemplate[]): Promise<CampaignTemplate[]> => {
  const latest = await latestThemeSettings()
  const next = update(templatesIn(latest))
  const res = await BusinessModel.updateBusinessProfile({ body: { theme_settings: { ...latest, [CAMPAIGN_TEMPLATES_KEY]: next } } })
  if (!res.success) throw new Error('Could not save the template')
  return next
}

/** The store's saved templates; templates still in this browser are moved to the server once. */
export const loadCampaignTemplates = async (): Promise<CampaignTemplate[]> => {
  const legacy = takeLegacy()
  if (!legacy.length) return templatesIn(await latestThemeSettings())
  const merged = await write((current) => [...current, ...legacy.filter((l) => !current.some((c) => c.id === l.id))])
  try {
    localStorage.removeItem(legacyKey())
  } catch {
    // Already on the server; a leftover local copy is merged again harmlessly next time.
  }
  return merged
}

export const saveCampaignTemplate = (template: CampaignTemplate) =>
  write((current) => [template, ...current.filter((t) => t.id !== template.id)])

export const deleteCampaignTemplate = (id: string) => write((current) => current.filter((t) => t.id !== id))
